import React, { useState } from 'react';
import { Hash, Copy, Trash2, ArrowLeftRight } from 'lucide-react';

const AsciiTextTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('textToAscii'); // textToAscii, asciiToText
  const [format, setFormat] = useState('decimal'); // decimal, hex, binary
  const [separator, setSeparator] = useState('space'); // space, comma, newline
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const getSeparatorChar = () => {
    switch (separator) {
      case 'comma': return ', ';
      case 'newline': return '\n';
      default: return ' ';
    }
  };

  const convert = () => {
    setError('');
    setOutput('');

    if (!input.trim()) {
      setError('Please enter a value');
      return;
    }

    try {
      if (mode === 'textToAscii') {
        const result = [...input].map(char => {
          const code = char.charCodeAt(0);
          switch (format) {
            case 'hex': return code.toString(16).toUpperCase().padStart(2, '0');
            case 'binary': return code.toString(2).padStart(8, '0');
            default: return code.toString();
          }
        });
        setOutput(result.join(getSeparatorChar()));
      } else {
        // Parse ASCII to text
        let sep = getSeparatorChar().trim() || ' ';
        let codes;
        
        if (format === 'binary') {
          // Split by separator or every 8 chars
          codes = input.includes(sep) 
            ? input.split(sep).map(s => parseInt(s.trim(), 2))
            : input.match(/.{1,8}/g)?.map(s => parseInt(s, 2)) || [];
        } else if (format === 'hex') {
          codes = input.includes(sep)
            ? input.split(sep).map(s => parseInt(s.trim(), 16))
            : input.match(/.{1,2}/g)?.map(s => parseInt(s, 16)) || [];
        } else {
          codes = input.split(/[\s,]+/).map(s => parseInt(s.trim(), 10));
        }

        // Validate codes
        if (codes.some(c => isNaN(c) || c < 0 || c > 127)) {
          setError('Invalid ASCII code detected. Values must be 0-127.');
          return;
        }

        setOutput(String.fromCharCode(...codes));
      }
    } catch (e) {
      setError('Conversion error: ' + e.message);
    }
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const swapMode = () => {
    setMode(mode === 'textToAscii' ? 'asciiToText' : 'textToAscii');
    if (output) {
      setInput(output);
      setOutput('');
    }
  };

  const loadSample = () => {
    if (mode === 'textToAscii') {
      setInput('Hello World!');
    } else {
      switch (format) {
        case 'hex':
          setInput('48 65 6C 6C 6F 20 57 6F 72 6C 64 21');
          break;
        case 'binary':
          setInput('01001000 01100101 01101100 01101100 01101111');
          break;
        default:
          setInput('72 101 108 108 111 32 87 111 114 108 100 33');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
        <div className={`flex-1 text-center ${mode === 'textToAscii' ? 'text-blue-400' : 'text-gray-400'}`}>
          <div className="text-sm font-medium">Text</div>
        </div>
        <button
          onClick={swapMode}
          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          title="Swap direction"
        >
          <ArrowLeftRight size={20} />
        </button>
        <div className={`flex-1 text-center ${mode === 'asciiToText' ? 'text-blue-400' : 'text-gray-400'}`}>
          <div className="text-sm font-medium">ASCII</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="decimal">Decimal (65, 66, 67)</option>
            <option value="hex">Hexadecimal (41, 42, 43)</option>
            <option value="binary">Binary (01000001)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Separator</label>
          <select
            value={separator}
            onChange={(e) => setSeparator(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="space">Space</option>
            <option value="comma">Comma</option>
            <option value="newline">New Line</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-300">
            {mode === 'textToAscii' ? 'Text Input' : 'ASCII Input'}
          </label>
          <button onClick={loadSample} className="text-xs text-blue-400 hover:text-blue-300">
            Load Sample
          </button>
        </div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={mode === 'textToAscii' ? 'Enter text...' : 'Enter ASCII codes...'}
          className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      <button
        onClick={convert}
        disabled={!input.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <Hash size={18} />
        Convert
      </button>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {mode === 'textToAscii' ? 'ASCII Output' : 'Text Output'}
          </label>
          <div className="relative">
            <textarea
              value={output}
              readOnly
              className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono"
            />
            <button
              onClick={copyOutput}
              className={`absolute top-2 right-2 p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={16} />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">ASCII Reference</h3>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-xs font-mono">
          {['A=65', 'B=66', 'Z=90', 'a=97', 'z=122', '0=48', '9=57', ' =32'].map((ref) => (
            <div key={ref} className="p-1 bg-gray-900 rounded text-center text-gray-400">
              {ref}
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => { setInput(''); setOutput(''); setError(''); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default AsciiTextTool;
